import { useEffect, useRef } from 'react';
import { inject, mk } from './kernel';

export interface HairlineContext {
  stage: HTMLElement;
  svg: SVGSVGElement;
  read: { textContent: string };
}

export interface HairlineHandle {
  set: (v: number) => void;
  destroy: () => void;
}

export type HairlineMount = (ctx: HairlineContext, value: number) => HairlineHandle;

interface HairlineFigureProps {
  name: string;
  means: string;
  mount: HairlineMount;
  stagger: number;
}

/* React host for a Hairline figure: one 5:4 stage, one 400×320 svg, cleaned up on unmount. */
export default function HairlineFigure({
  name, means, mount, stagger,
}: HairlineFigureProps) {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const stage = ref.current;
    if (!stage) return undefined;
    inject(document);
    const svg = mk('svg', { viewBox: '0 0 400 320', 'aria-hidden': 'true' }, stage) as SVGSVGElement;
    const handle = mount({ stage, svg, read: { textContent: 'rest' } }, stagger);
    return () => {
      handle.destroy();
      svg.remove();
    };
  }, [mount, stagger]);

  return (
    <div
      ref={ref}
      data-hairline={name}
      role="img"
      aria-label={means}
      className="hl-stage"
    />
  );
}